/**
 * Geometry Tools - Core Service
 * Fundamental geometric and spatial analysis tools
 *
 * This core service provides basic geometric operations used by higher-level validators:
 *
 * DISTANCE MEASUREMENT:
 * - Center-to-center: Distance between element centroids
 * - Surface-to-surface: Closest distance between element surfaces
 * - Center-to-surface: From one element's center to another's surface
 *
 * BOUNDING BOX OPERATIONS:
 * - Extract 3D bounding boxes for elements
 * - Extend boxes by specified amounts
 * - Box intersection and union operations
 * - Closest point calculations
 *
 * SPATIAL QUERIES:
 * - Find intersecting elements within bounds
 * - Elements within radius searches
 * - Level/floor-based filtering
 * - Volume and surface area calculations
 *
 * GEOMETRIC CALCULATIONS:
 * - Ray-box intersections
 * - Point-to-box distances
 * - Box-to-box relationships
 * - Clearance calculations
 *
 * All methods are static, stateless, and optimized for performance.
 * This is a core service - it does NOT implement specific compliance rules.
 */

/**
 * Unit Conversion Utilities
 * Revit/Forge Viewer internally uses FEET as the base unit
 * All geometry calculations should be done in FEET
 * Convert to/from other units only at UI boundaries
 */
export class UnitConversion {
  static readonly FEET_TO_METERS = 0.3048;
  static readonly METERS_TO_FEET = 3.28084;
  static readonly FEET_TO_INCHES = 12;
  static readonly INCHES_TO_FEET = 1 / 12;
  static readonly FEET_TO_MM = 304.8;
  static readonly MM_TO_FEET = 1 / 304.8;

  static feetToMeters(feet: number): number {
    return feet * this.FEET_TO_METERS;
  }

  static metersToFeet(meters: number): number {
    return meters * this.METERS_TO_FEET;
  }

  static feetToInches(feet: number): number {
    return feet * this.FEET_TO_INCHES;
  }

  static inchesToFeet(inches: number): number {
    return inches * this.INCHES_TO_FEET;
  }

  static feetToMillimeters(feet: number): number {
    return feet * this.FEET_TO_MM;
  }

  static millimetersToFeet(mm: number): number {
    return mm * this.MM_TO_FEET;
  }
}

export interface BoundingBoxExtended {
  original: THREE.Box3;
  extended: THREE.Box3;
  extension: THREE.Vector3;
}

export interface DistanceMeasurement {
  distance: number;
  point1: THREE.Vector3;
  point2: THREE.Vector3;
  type: "center-to-center" | "surface-to-surface" | "center-to-surface";
}

export interface ElementRelationship {
  element1: number;
  element2: number;
  relationship:
    | "adjacent"
    | "contains"
    | "contained-by"
    | "intersects"
    | "separated";
  distance?: number;
}

export interface PathAnalysis {
  minWidth: number;
  minHeight: number;
  obstacles: number[];
  clearancePoints: Array<{
    point: THREE.Vector3;
    clearance: number;
  }>;
}

export interface RayCastHit {
  dbId: number;
  distance: number;
  point: THREE.Vector3;
  face?: any;
  fragId?: number;
}

export interface RayCastResult {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  maxDistance: number;
  sourceDbId?: number;
  hits: RayCastHit[];
  hitCount: number;
}

export interface MultiHitRayCastResult {
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  maxDistance: number;
  sourceDbId?: number;
  allHits: RayCastHit[]; // All hits sorted by distance
  hitCount: number;
}

export interface ElementOrientation {
  forward: THREE.Vector3;
  up: THREE.Vector3;
  right: THREE.Vector3;
  confidence: number;
}

export interface MultiRayCastResult {
  rayId: string;
  origin: THREE.Vector3;
  direction: THREE.Vector3;
  directionName: string;
  height: number;
  hits: RayCastHit[];
  maxDistance: number;
  hasHit: boolean;
}

export class GeometryTools {
  /**
   * Helper function to get Forge Viewer's internal THREE.js instance
   */
  private static getTHREE(viewer: Autodesk.Viewing.GuiViewer3D): any {
    return (window as any).THREE || (viewer as any).impl?.renderer?.THREE;
  }

  /**
   * Measure distance between two elements (multiple modes)
   * @param viewer - Forge viewer instance
   * @param dbId1 - First element ID
   * @param dbId2 - Second element ID
   * @param mode - Measurement mode (center-to-center, surface-to-surface, center-to-surface)
   * @returns Distance measurement in FEET, or null if elements not found
   */
  static measureDistance(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId1: number,
    dbId2: number,
    mode:
      | "center-to-center"
      | "surface-to-surface"
      | "center-to-surface" = "center-to-center"
  ): DistanceMeasurement | null {
    if (!viewer || !viewer.model) return null;

    const box1 = this.getElementBoundingBox(viewer, dbId1);
    const box2 = this.getElementBoundingBox(viewer, dbId2);

    if (!box1 || !box2) return null;

    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return null;
    }

    const center1 = new THREE.Vector3();
    const center2 = new THREE.Vector3();
    box1.getCenter(center1);
    box2.getCenter(center2);

    let distance: number;
    let point1: THREE.Vector3;
    let point2: THREE.Vector3;

    switch (mode) {
      case "center-to-center":
        distance = center1.distanceTo(center2);
        point1 = center1;
        point2 = center2;
        break;

      case "surface-to-surface":
        // Find closest points on bounding boxes
        const closestPoints = this.getClosestPointsBetweenBoxes(box1, box2);
        distance = closestPoints.point1.distanceTo(closestPoints.point2);
        point1 = closestPoints.point1;
        point2 = closestPoints.point2;
        break;

      case "center-to-surface":
        // Center of first element to surface of second
        point1 = center1;
        point2 = this.getClosestPointOnBox(center1, box2);
        distance = point1.distanceTo(point2);

        // Verify the point is actually on or within the box
        console.log("[GeometryTools] Center-to-surface measurement:", {
          sourceCenter: { x: center1.x, y: center1.y, z: center1.z },
          targetBox: {
            min: { x: box2.min.x, y: box2.min.y, z: box2.min.z },
            max: { x: box2.max.x, y: box2.max.y, z: box2.max.z },
          },
          closestPoint: { x: point2.x, y: point2.y, z: point2.z },
          distance: distance,
          isOnSurface: this.isPointOnBoxSurface(point2, box2),
        });
        break;
    }

    return { distance, point1, point2, type: mode };
  }

  /**
   * Get closest points between two bounding boxes
   */
  private static getClosestPointsBetweenBoxes(
    box1: THREE.Box3,
    box2: THREE.Box3
  ): { point1: THREE.Vector3; point2: THREE.Vector3 } {
    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return { point1: new THREE.Vector3(), point2: new THREE.Vector3() };
    }

    // Check if boxes intersect
    if (box1.intersectsBox(box2)) {
      // If boxes intersect, find the intersection region and return the same point
      // This ensures surface-to-surface distance is 0 when objects overlap
      const intersectionBox = new THREE.Box3();
      intersectionBox.min.x = Math.max(box1.min.x, box2.min.x);
      intersectionBox.min.y = Math.max(box1.min.y, box2.min.y);
      intersectionBox.min.z = Math.max(box1.min.z, box2.min.z);
      intersectionBox.max.x = Math.min(box1.max.x, box2.max.x);
      intersectionBox.max.y = Math.min(box1.max.y, box2.max.y);
      intersectionBox.max.z = Math.min(box1.max.z, box2.max.z);

      // Return the center of intersection as the same point for both
      // This gives us 0 distance for intersecting boxes
      const intersectionCenter = new THREE.Vector3();
      intersectionBox.getCenter(intersectionCenter);
      return { point1: intersectionCenter, point2: intersectionCenter };
    }

    // Find the closest points when boxes don't intersect
    // The closest point on box1 to box2 is found by clamping box2's center to box1
    // The closest point on box2 to box1 is found by clamping box1's center to box2

    const center1 = new THREE.Vector3();
    const center2 = new THREE.Vector3();
    box1.getCenter(center1);
    box2.getCenter(center2);

    // Find closest point on box1 to box2's center
    const closestOnBox1 = new THREE.Vector3();
    closestOnBox1.copy(center2);
    closestOnBox1.clamp(box1.min, box1.max);

    // Find closest point on box2 to box1's center
    const closestOnBox2 = new THREE.Vector3();
    closestOnBox2.copy(center1);
    closestOnBox2.clamp(box2.min, box2.max);

    // Now we need to find the actual closest points
    // Check all possible closest point pairs and find the minimum distance
    const candidates = [
      // Original approach
      { p1: closestOnBox1, p2: closestOnBox2 },

      // Also check from the closest point on box1 to find the closest on box2
      {
        p1: closestOnBox1,
        p2: this.getClosestPointOnBox(closestOnBox1, box2),
      },

      // And from the closest point on box2 to find the closest on box1
      {
        p1: this.getClosestPointOnBox(closestOnBox2, box1),
        p2: closestOnBox2,
      },
    ];

    // Find the pair with minimum distance
    let minDistance = Infinity;
    let bestPair = candidates[0];

    for (const pair of candidates) {
      const dist = pair.p1.distanceTo(pair.p2);
      if (dist < minDistance) {
        minDistance = dist;
        bestPair = pair;
      }
    }

    return { point1: bestPair.p1, point2: bestPair.p2 };
  }

  /**
   * Get closest point on a box to a given point
   */
  static getClosestPointOnBox(
    point: THREE.Vector3,
    box: THREE.Box3
  ): THREE.Vector3 {
    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return new THREE.Vector3();
    }

    const result = new THREE.Vector3();
    result.copy(point);
    result.clamp(box.min, box.max);
    return result;
  }

  /**
   * Check if a point is on the surface of a box (not inside)
   */
  private static isPointOnBoxSurface(
    point: THREE.Vector3,
    box: THREE.Box3,
    tolerance: number = 0.001
  ): boolean {
    // A point is on the surface if at least one coordinate equals the box boundary
    const onMinX = Math.abs(point.x - box.min.x) < tolerance;
    const onMaxX = Math.abs(point.x - box.max.x) < tolerance;
    const onMinY = Math.abs(point.y - box.min.y) < tolerance;
    const onMaxY = Math.abs(point.y - box.max.y) < tolerance;
    const onMinZ = Math.abs(point.z - box.min.z) < tolerance;
    const onMaxZ = Math.abs(point.z - box.max.z) < tolerance;

    return onMinX || onMaxX || onMinY || onMaxY || onMinZ || onMaxZ;
  }

  /**
   * 2. BOUNDING BOX TOOLS
   */

  /**
   * Get element's bounding box
   * @param viewer - Forge viewer instance
   * @param dbId - Element ID
   * @returns Bounding box with coordinates in FEET, or null if element not found
   */
  static getElementBoundingBox(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): THREE.Box3 | null {
    if (!viewer || !viewer.model) return null;

    const fragList = viewer.model.getFragmentList();
    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return null;
    }

    const box = new THREE.Box3();
    let initialized = false;

    const instanceTree = viewer.model.getInstanceTree();
    if (instanceTree) {
      instanceTree.enumNodeFragments(
        dbId,
        (fragId: number) => {
          const fragBox = new THREE.Box3();
          fragList.getWorldBounds(fragId, fragBox);

          if (!initialized) {
            box.copy(fragBox);
            initialized = true;
          } else {
            box.union(fragBox);
          }
        },
        true
      );
    }

    return initialized ? box : null;
  }

  /**
   * Extend bounding box by specified amount
   * @param box - Original bounding box (in FEET)
   * @param extension - Extension amount in FEET (number or Vector3)
   * @returns Extended bounding box data
   */
  static extendBoundingBox(
    box: THREE.Box3,
    extension: number | THREE.Vector3
  ): BoundingBoxExtended {
    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return {
        original: box,
        extended: box.clone(),
        extension: new THREE.Vector3(),
      };
    }

    const extended = box.clone();
    const extensionVector =
      typeof extension === "number"
        ? new THREE.Vector3(extension, extension, extension)
        : extension;

    extended.min.sub(extensionVector);
    extended.max.add(extensionVector);

    return {
      original: box,
      extended: extended,
      extension: extensionVector,
    };
  }

  /**
   * Find all elements that intersect with an extended bounding box
   */
  static findIntersectingElements(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    extension: number | THREE.Vector3
  ): number[] {
    if (!viewer || !viewer.model) return [];

    const sourceBox = this.getElementBoundingBox(viewer, dbId);
    if (!sourceBox) return [];

    const extendedBox = this.extendBoundingBox(sourceBox, extension);
    const intersectingDbIds: number[] = [];

    const tree = viewer.model.getInstanceTree();
    if (!tree) return [];

    // Check all elements
    const allDbIds: number[] = [];
    tree.enumNodeChildren(
      tree.getRootId(),
      (childId) => {
        allDbIds.push(childId);
      },
      true
    );

    allDbIds.forEach((testDbId) => {
      if (testDbId === dbId) return; // Skip self

      const testBox = this.getElementBoundingBox(viewer, testDbId);
      if (testBox && extendedBox.extended.intersectsBox(testBox)) {
        intersectingDbIds.push(testDbId);
      }
    });

    return intersectingDbIds;
  }

  /**
   * 3. SPATIAL RELATIONSHIP TOOLS
   */

  /**
   * Determine spatial relationship between two elements
   */
  static getElementRelationship(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId1: number,
    dbId2: number
  ): ElementRelationship | null {
    const box1 = this.getElementBoundingBox(viewer, dbId1);
    const box2 = this.getElementBoundingBox(viewer, dbId2);

    if (!box1 || !box2) return null;

    let relationship: ElementRelationship["relationship"];
    let distance: number | undefined;

    if (box1.containsBox(box2)) {
      relationship = "contains";
    } else if (box2.containsBox(box1)) {
      relationship = "contained-by";
    } else if (box1.intersectsBox(box2)) {
      relationship = "intersects";
    } else {
      relationship = "separated";
      const measurement = this.measureDistance(
        viewer,
        dbId1,
        dbId2,
        "surface-to-surface"
      );
      distance = measurement?.distance;

      // Check if they're adjacent (very close but not intersecting)
      if (distance && distance < 0.1) {
        // 0.1 units threshold for adjacency
        relationship = "adjacent";
      }
    }

    return {
      element1: dbId1,
      element2: dbId2,
      relationship,
      distance,
    };
  }

  /**
   * Find elements within a specific radius
   * @param viewer - Forge viewer instance
   * @param dbId - Source element ID
   * @param radius - Search radius in FEET
   * @returns Array of elements with their distances in FEET
   */
  static findElementsWithinRadius(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    radius: number
  ): Array<{ dbId: number; distance: number }> {
    const sourceBox = this.getElementBoundingBox(viewer, dbId);
    if (!sourceBox) return [];

    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return [];
    }

    const sourceCenter = sourceBox.getCenter(new THREE.Vector3());
    const results: Array<{ dbId: number; distance: number }> = [];

    const tree = viewer.model.getInstanceTree();
    if (!tree) return [];

    const allDbIds: number[] = [];
    tree.enumNodeChildren(
      tree.getRootId(),
      (childId) => {
        allDbIds.push(childId);
      },
      true
    );

    allDbIds.forEach((testDbId) => {
      if (testDbId === dbId) return;

      const testBox = this.getElementBoundingBox(viewer, testDbId);
      if (testBox) {
        // Use center-to-surface distance for consistency with other measurements
        const closestPoint = this.getClosestPointOnBox(sourceCenter, testBox);
        const distance = sourceCenter.distanceTo(closestPoint);

        if (distance <= radius) {
          results.push({ dbId: testDbId, distance });
        }
      }
    });

    return results.sort((a, b) => a.distance - b.distance);
  }

  /**
   * 4. PATH & CLEARANCE ANALYSIS
   */

  /**
   * Analyze clearance along a path between two points
   * @param viewer - Forge viewer instance
   * @param startPoint - Path start point (coordinates in FEET)
   * @param endPoint - Path end point (coordinates in FEET)
   * @param checkWidth - Required path width in FEET
   * @param checkHeight - Required path height in FEET
   * @returns Path analysis with clearances in FEET
   */
  static analyzePathClearance(
    viewer: Autodesk.Viewing.GuiViewer3D,
    startPoint: THREE.Vector3,
    endPoint: THREE.Vector3,
    checkWidth: number,
    checkHeight: number
  ): PathAnalysis {
    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return { obstacles: [], clearancePoints: [], minWidth: 0, minHeight: checkHeight };
    }

    const direction = new THREE.Vector3()
      .subVectors(endPoint, startPoint)
      .normalize();
    const pathLength = startPoint.distanceTo(endPoint);
    const perpendicular = new THREE.Vector3(
      -direction.z,
      0,
      direction.x
    ).normalize();

    const obstacles: number[] = [];
    const clearancePoints: PathAnalysis["clearancePoints"] = [];
    let minWidth = Infinity;

    // Sample points along the path
    const samples = Math.ceil(pathLength / 0.5); // Sample every 0.5 units

    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const samplePoint = new THREE.Vector3().lerpVectors(
        startPoint,
        endPoint,
        t
      );

      // Create a box at this point to check clearance
      const checkBox = new THREE.Box3();
      checkBox.min.set(
        samplePoint.x - checkWidth / 2,
        samplePoint.y,
        samplePoint.z - checkWidth / 2
      );
      checkBox.max.set(
        samplePoint.x + checkWidth / 2,
        samplePoint.y + checkHeight,
        samplePoint.z + checkWidth / 2
      );

      // Find intersecting elements
      const tree = viewer.model.getInstanceTree();
      if (tree) {
        const allDbIds: number[] = [];
        tree.enumNodeChildren(
          tree.getRootId(),
          (childId) => {
            allDbIds.push(childId);
          },
          true
        );

        allDbIds.forEach((dbId) => {
          const elementBox = this.getElementBoundingBox(viewer, dbId);
          if (elementBox && checkBox.intersectsBox(elementBox)) {
            if (!obstacles.includes(dbId)) {
              obstacles.push(dbId);
            }

            // Calculate actual clearance
            const clearance = this.calculateClearanceFromPoint(
              samplePoint,
              elementBox,
              perpendicular
            );
            clearancePoints.push({
              point: samplePoint,
              clearance,
            });
            minWidth = Math.min(minWidth, clearance);
          }
        });
      }
    }

    return {
      minWidth: minWidth === Infinity ? checkWidth : minWidth,
      minHeight: checkHeight,
      obstacles,
      clearancePoints,
    };
  }

  /**
   * Calculate clearance from a point to a box along a direction
   */
  private static calculateClearanceFromPoint(
    point: THREE.Vector3,
    box: THREE.Box3,
    direction: THREE.Vector3
  ): number {
    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return 0;
    }

    const ray = new THREE.Ray(point, direction);
    const intersection = ray.intersectBox(box, new THREE.Vector3());

    if (intersection) {
      return point.distanceTo(intersection);
    }

    // If no intersection, return distance to closest point
    const closest = this.getClosestPointOnBox(point, box);
    return point.distanceTo(closest);
  }

  /**
   * 5. UTILITY FUNCTIONS
   */

  /**
   * Get element volume
   */
  static getElementVolume(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): number {
    const box = this.getElementBoundingBox(viewer, dbId);
    if (!box) return 0;

    const size = box.size();
    return size.x * size.y * size.z;
  }

  /**
   * Get element surface area (approximate from bounding box)
   */
  static getElementSurfaceArea(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): number {
    const box = this.getElementBoundingBox(viewer, dbId);
    if (!box) return 0;

    const size = box.size();
    return 2 * (size.x * size.y + size.x * size.z + size.y * size.z);
  }

  /**
   * Check if element is on specific level/floor
   */
  static isElementOnLevel(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    levelMin: number,
    levelMax: number
  ): boolean {
    const box = this.getElementBoundingBox(viewer, dbId);
    if (!box) return false;

    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return false;
    }

    const center = new THREE.Vector3();
    box.getCenter(center);
    return center.y >= levelMin && center.y <= levelMax;
  }

  /**
   * Parse numeric value from property string
   * Extracts numeric value from strings like "36 in" or "8.5%"
   * Consolidates duplicate implementations from validators
   */
  static parseNumericValue(value: any): number {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      // Extract numeric value from strings with units
      const match = value.match(/[\d.]+/);
      return match ? parseFloat(match[0]) : 0;
    }
    return 0;
  }

  /**
   * Get element name from viewer
   * Centralized method for getting element names
   */
  static async getElementName(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): Promise<string> {
    const tree = viewer.model.getInstanceTree();
    if (!tree) return `Element ${dbId}`;

    return new Promise((resolve) => {
      viewer.getProperties(
        dbId,
        (result) => {
          // Try to get name from properties
          const nameProp = result?.properties?.find(
            (p: any) => p.displayName === "Name" || p.displayName === "Mark"
          );

          if (nameProp?.displayValue) {
            resolve(nameProp.displayValue);
          } else {
            // Fall back to tree node name
            const nodeName = tree.getNodeName(dbId);
            resolve(nodeName || `Element ${dbId}`);
          }
        },
        () => {
          // Error callback - use tree name or default
          const nodeName = tree.getNodeName(dbId);
          resolve(nodeName || `Element ${dbId}`);
        }
      );
    });
  }

  /**
   * Get elements within radius of a point
   * Useful for proximity-based validation
   */
  static getElementsWithinRadius(
    viewer: Autodesk.Viewing.GuiViewer3D,
    center: THREE.Vector3,
    radius: number,
    excludeDbIds: number[] = []
  ): number[] {
    const elementsInRadius: number[] = [];
    const excludeSet = new Set(excludeDbIds);

    const tree = viewer.model.getInstanceTree();
    if (!tree) return elementsInRadius;

    const allDbIds: number[] = [];
    tree.enumNodeChildren(
      tree.getRootId(),
      (dbId) => {
        if (!excludeSet.has(dbId)) {
          allDbIds.push(dbId);
        }
      },
      true
    );

    allDbIds.forEach((dbId) => {
      const box = this.getElementBoundingBox(viewer, dbId);
      if (box) {
        // Note: This method doesn't have access to viewer, so we'll use window.THREE
        const THREE = (window as any).THREE;
        if (!THREE) {
          console.error("[GeometryTools] THREE.js not available");
          return elementsInRadius;
        }

        const elementCenter = new THREE.Vector3();
        box.getCenter(elementCenter);
        const distance = center.distanceTo(elementCenter);

        if (distance <= radius) {
          elementsInRadius.push(dbId);
        }
      }
    });

    return elementsInRadius;
  }

  /**
   * Calculate clearance between element and point
   * Returns minimum distance from point to element surface
   */
  static calculateClearance(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    point: THREE.Vector3
  ): number {
    const box = this.getElementBoundingBox(viewer, dbId);
    if (!box) return Infinity;

    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return Infinity;
    }

    // Calculate distance from point to nearest face of bounding box
    const clampedPoint = new THREE.Vector3(
      Math.max(box.min.x, Math.min(point.x, box.max.x)),
      Math.max(box.min.y, Math.min(point.y, box.max.y)),
      Math.max(box.min.z, Math.min(point.z, box.max.z))
    );

    return point.distanceTo(clampedPoint);
  }

  /**
   * Cast a ray and find intersecting elements
   * @param viewer - Forge viewer instance
   * @param origin - Ray origin point in world coordinates
   * @param direction - Ray direction (normalized)
   * @param maxDistance - Maximum distance for ray casting
   * @param sourceDbId - Element to exclude from intersections (typically the source element)
   * @param model - The viewer model
   * @returns Ray casting result with all intersections
   */
  static castRay(
    viewer: Autodesk.Viewing.GuiViewer3D,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    maxDistance: number,
    sourceDbId?: number,
    model?: any
  ): RayCastResult | null {
    // Removed excessive debug logging - only keep essential error logs
    
    if (!viewer || !viewer.impl) {
      console.error("[GeometryTools] Viewer or viewer.impl not available");
      return null;
    }

    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return null;
    }

    // Use the model parameter or default to viewer.model
    const targetModel = model || viewer.model;
    if (!targetModel) {
      console.error("[GeometryTools] No model available");
      return null;
    }

    // Validate inputs
    if (!origin || origin.x === undefined || origin.y === undefined || origin.z === undefined) {
      console.error("[GeometryTools] Invalid origin:", origin);
      return null;
    }
    
    if (!direction || direction.x === undefined || direction.y === undefined || direction.z === undefined) {
      console.error("[GeometryTools] Invalid direction:", direction);
      return null;
    }

    try {
      const intersections = [];
      let hitResult = null;

      // Use manual intersection with bounding boxes as primary method
      hitResult = this.manualRayIntersection(viewer, origin, direction, maxDistance, sourceDbId);

      // Process hit result - now handles array of hits
      if (hitResult && hitResult.length > 0) {
        // Process all hits from manual method
        for (const hit of hitResult) {
          let dbId, distance, point;
          
          if (hit.dbId !== undefined) {
            dbId = hit.dbId;
            distance = hit.distance || 0;
            point = hit.point || hit.intersectPoint || (origin as any).clone();
          }

          if (dbId !== undefined && dbId !== sourceDbId && distance <= maxDistance) {
            intersections.push({
              dbId: dbId,
              distance: distance,
              point: (point as any).clone ? (point as any).clone() : new THREE.Vector3(point.x, point.y, point.z),
              face: hit.face,
              fragId: hit.fragId
            });
          }
        }
      } else if (hitResult && hitResult.dbId !== undefined) {
        // Handle single hit for backward compatibility
        let dbId, distance, point;
        
        dbId = hitResult.dbId;
        distance = hitResult.distance || 0;
        point = hitResult.point || hitResult.intersectPoint || (origin as any).clone();

        if (dbId !== undefined && dbId !== sourceDbId && distance <= maxDistance) {
          intersections.push({
            dbId: dbId,
            distance: distance,
            point: (point as any).clone ? (point as any).clone() : new THREE.Vector3(point.x, point.y, point.z),
            face: hitResult.face,
            fragId: hitResult.fragId
          });
        }
      }

      const result = {
        origin: (origin as any).clone(),
        direction: (direction as any).clone(),
        maxDistance: maxDistance,
        sourceDbId: sourceDbId,
        hits: intersections,
        hitCount: intersections.length
      };

      return result;
    } catch (error) {
      console.error("[GeometryTools] Ray casting error:", error);
      return null;
    }
  }

  /**
   * Cast a ray and return ALL intersecting elements (not just the closest)
   */
  static castMultiHitRay(
    viewer: Autodesk.Viewing.GuiViewer3D,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    maxDistance: number,
    sourceDbId?: number
  ): MultiHitRayCastResult | null {
    // Removed excessive debug logging - only keep essential error logs

    if (!viewer || !viewer.impl) {
      console.error("[GeometryTools] Viewer or viewer.impl not available");
      return null;
    }

    const THREE = this.getTHREE(viewer);
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return null;
    }

    try {
      const allHits: RayCastHit[] = [];
      
      // Use manual intersection method
      const hitResult = this.manualRayIntersection(viewer, origin, direction, maxDistance, sourceDbId);
      
      if (hitResult && Array.isArray(hitResult)) {
        for (const hit of hitResult) {
          if (hit.dbId !== undefined && hit.dbId !== sourceDbId) {
            const distance = hit.distance || 0;
            if (distance <= maxDistance) {
              // Removed per-intersection logging
              allHits.push({
                dbId: hit.dbId,
                distance: distance,
                point: hit.point || hit.intersectPoint,
                face: hit.face,
                fragId: hit.fragId
              });
            }
          }
        }
      }

      // Sort by distance (closest first)
      allHits.sort((a, b) => a.distance - b.distance);
      
      // Removed detailed result logging
      
      return {
        origin: (origin as any).clone(),
        direction: (direction as any).clone(),
        maxDistance: maxDistance,
        sourceDbId: sourceDbId,
        allHits: allHits,
        hitCount: allHits.length,
        hasHit: allHits.length > 0,
        closestHit: allHits.length > 0 ? allHits[0] : null
      };
    } catch (error) {
      console.error("[GeometryTools] Multi-hit ray casting error:", error);
      return null;
    }
  }

  /**
   * Manual ray intersection using bounding boxes as fallback
   */
  private static manualRayIntersection(
    viewer: Autodesk.Viewing.GuiViewer3D,
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    maxDistance: number,
    sourceDbId?: number
  ): any {
    // Removed excessive debug logging
    
    const THREE = this.getTHREE(viewer);
    const tree = viewer.model.getInstanceTree();
    
    if (!tree) {
      console.error("[GeometryTools] No instance tree available");
      return null;
    }

    // Get all elements
    const allDbIds: number[] = [];
    tree.enumNodeChildren(tree.getRootId(), (dbId) => {
      if (dbId !== sourceDbId) {
        allDbIds.push(dbId);
      }
    }, true);

    // Only log for very small models or if no elements found
    if (allDbIds.length === 0) {
      console.warn("[GeometryTools] No elements found for ray intersection");
      return null;
    }

    let closestHit = null;
    let closestDistance = Infinity;

    const ray = new THREE.Ray(origin, direction);

    const allHits: any[] = [];

    for (const dbId of allDbIds) {
      const box = this.getElementBoundingBox(viewer, dbId);
      if (!box) continue;

      // Test ray-box intersection
      const intersectionPoint = new THREE.Vector3();
      const intersects = ray.intersectBox(box, intersectionPoint);

      if (intersects) {
        const distance = (origin as any).distanceTo(intersectionPoint);
        
        // Removed per-element logging - was generating thousands of logs
        
        if (distance <= maxDistance) {
          allHits.push({
            dbId: dbId,
            distance: distance,
            point: intersectionPoint,
            intersectPoint: intersectionPoint
          });
          
          // Keep track of closest hit for backward compatibility
          if (distance < closestDistance) {
            closestDistance = distance;
            closestHit = {
              dbId: dbId,
              distance: distance,
              point: intersectionPoint,
              intersectPoint: intersectionPoint
            };
          }
        }
      }
    }

    // Sort hits by distance
    allHits.sort((a, b) => a.distance - b.distance);
    
    // Only log summary - not individual hits
    // Removed detailed logging that was generating excessive output
    return allHits;
  }

  /**
   * Get element orientation using PCA analysis
   * @param viewer - Forge viewer instance
   * @param dbId - Element ID
   * @returns Element orientation vectors or null
   */
  static async getElementOrientation(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number
  ): Promise<ElementOrientation | null> {
    try {
      // Dynamic import to avoid circular dependencies
      const { PCAOrientationTool } = await import("@/services/tools/PCAOrientationTool");
      
      // Use PCA tool to get orientation
      const result = await PCAOrientationTool.analyzeAndVisualizeElementOrientation(
        dbId,
        viewer,
        { 
          arrowLength: 0, // Don't show arrow for ray casting
          minConfidence: 0.3
        }
      );

      if (!result) {
        // Fallback to simple bounding box based orientation
        const box = this.getElementBoundingBox(viewer, dbId);
        if (!box) return null;

        const THREE = this.getTHREE(viewer);
        const size = box.getSize(new THREE.Vector3());
        
        // Determine primary axis based on largest dimension
        let forward: THREE.Vector3;
        let up = new THREE.Vector3(0, 0, 1); // Default up is Z
        
        if (size.x >= size.y && size.x >= size.z) {
          // X is largest - element is oriented along X
          forward = new THREE.Vector3(1, 0, 0);
        } else if (size.y >= size.x && size.y >= size.z) {
          // Y is largest - element is oriented along Y  
          forward = new THREE.Vector3(0, 1, 0);
        } else {
          // Z is largest - element is oriented along Z
          forward = new THREE.Vector3(0, 0, 1);
          up = new THREE.Vector3(0, 1, 0); // Change up to Y
        }
        
        const right = new THREE.Vector3().crossVectors(up, forward).normalize();
        
        return {
          forward: forward,
          up: up,
          right: right,
          confidence: 0.5 // Low confidence for bounding box method
        };
      }

      // Convert PCA result to orientation vectors
      const THREE = this.getTHREE(viewer);
      const forward = result.direction.clone();
      const up = new THREE.Vector3(0, 0, 1); // Assume Z is up
      const right = new THREE.Vector3().crossVectors(up, forward).normalize();
      
      // Recalculate up to ensure orthogonality
      const correctedUp = new THREE.Vector3().crossVectors(forward, right).normalize();

      return {
        forward: forward,
        up: correctedUp, 
        right: right,
        confidence: result.confidence
      };
    } catch (error) {
      console.error("[GeometryTools] Error getting element orientation:", error);
      return null;
    }
  }

  /**
   * Cast multiple rays from an element in different directions and heights
   * @param viewer - Forge viewer instance
   * @param dbId - Source element ID
   * @param config - Ray casting configuration
   * @returns Array of ray cast results
   */
  static async castMultipleRays(
    viewer: Autodesk.Viewing.GuiViewer3D,
    dbId: number,
    config: {
      maxDistance: number;
      heights?: number[];
      directions?: string[];
    }
  ): Promise<MultiRayCastResult[]> {
    const results: MultiRayCastResult[] = [];
    
    // Get element bounding box and center
    const box = this.getElementBoundingBox(viewer, dbId);
    if (!box) return results;

    const THREE = this.getTHREE(viewer);
    const center = box.getCenter(new THREE.Vector3());
    
    // Get element orientation
    const orientation = await this.getElementOrientation(viewer, dbId);
    if (!orientation) {
      console.warn("[GeometryTools] Could not determine element orientation");
      return results;
    }

    // Default heights in feet
    const heights = config.heights || [0.5, 1.5, 3.0, 5.0];
    
    // Define directions based on element orientation
    // Ensure we have valid THREE.Vector3 objects
    const forward = new THREE.Vector3(
      orientation.forward.x,
      orientation.forward.y,
      orientation.forward.z
    );
    const right = new THREE.Vector3(
      orientation.right.x,
      orientation.right.y,
      orientation.right.z
    );
    
    const directionVectors: { [key: string]: THREE.Vector3 } = {
      forward: forward.clone(),
      backward: forward.clone().negate(),
      right: right.clone(),
      left: right.clone().negate()
    };

    // Use provided directions or default to all four
    const directions = config.directions || Object.keys(directionVectors);

    // Cast rays for each height and direction combination
    let rayId = 0;
    for (const height of heights) {
      for (const dirName of directions) {
        const direction = directionVectors[dirName];
        if (!direction) continue;

        // Calculate ray origin (center + height offset)
        const origin = center.clone();
        origin.z += height; // Add height offset

        // Cast the ray
        const rayResult = this.castRay(
          viewer,
          origin,
          direction.normalize(),
          config.maxDistance,
          dbId
        );

        if (rayResult) {
          results.push({
            rayId: `ray-${rayId++}`,
            origin: origin,
            direction: direction,
            directionName: dirName,
            height: height,
            hits: rayResult.hits,
            maxDistance: config.maxDistance,
            hasHit: rayResult.hits.length > 0
          });
        }
      }
    }

    return results;
  }

  /**
   * Find nearest element of specific type
   */
  static async findNearestElementOfType(
    viewer: Autodesk.Viewing.GuiViewer3D,
    fromDbId: number,
    category: string
  ): Promise<{ dbId: number; distance: number } | null> {
    const sourceBox = this.getElementBoundingBox(viewer, fromDbId);
    if (!sourceBox) return null;

    // Note: This method doesn't have access to viewer, so we'll use window.THREE
    const THREE = (window as any).THREE;
    if (!THREE) {
      console.error("[GeometryTools] THREE.js not available");
      return null;
    }

    const sourceCenter = new THREE.Vector3();
    sourceBox.getCenter(sourceCenter);
    let nearest: { dbId: number; distance: number } | null = null;

    return new Promise((resolve) => {
      const tree = viewer.model.getInstanceTree();
      if (!tree) {
        resolve(null);
        return;
      }

      const allDbIds: number[] = [];
      tree.enumNodeChildren(
        tree.getRootId(),
        (childId) => {
          allDbIds.push(childId);
        },
        true
      );

      // Get properties for all elements
      viewer.model.getBulkProperties(allDbIds, ["Category"], (results) => {
        results.forEach((result) => {
          const categoryProp = result.properties.find(
            (p: any) => p.displayName === "Category"
          );
          if (categoryProp && categoryProp.displayValue === category) {
            const elementBox = this.getElementBoundingBox(viewer, result.dbId);
            if (elementBox) {
              // Note: This method doesn't have access to viewer, so we'll use window.THREE
              const THREE = (window as any).THREE;
              if (!THREE) {
                console.error("[GeometryTools] THREE.js not available");
                return;
              }

              const elementCenter = new THREE.Vector3();
              elementBox.getCenter(elementCenter);
              const distance = sourceCenter.distanceTo(elementCenter);
              if (!nearest || distance < nearest.distance) {
                nearest = { dbId: result.dbId, distance };
              }
            }
          }
        });
        resolve(nearest);
      });
    });
  }
}
